import { formatDate, MOMENT_FORMATS } from '../../../utils/dateFormat';
import { getEventTypeLabel } from '../eventTypes';
import { formatMultilineDisplayText } from './eventFormConstants';
import { EVENT_FORMAT, isOnlineEventFormat } from './eventFormatConstants';
import { formatTimeLabel } from './eventTimeUtils';
import {
  ATTENDEE_ELIGIBILITY,
  normalizeAttendeeEligibility,
} from '../../../shared/registrationSettings';
import { normalizeTopicsFromApi } from './eventTopicsConstants';

/** Member-app brand on EventDetailScreen (not partner-dashboard orange). */
export const APP_EVENT_PRIMARY = '#F08080';

/** Shared phone chrome for event and community live previews. */
export const LIVE_PREVIEW_PHONE_WIDTH = 340;
export const LIVE_PREVIEW_PHONE_HEIGHT = 700;

const APP_FORMAT_LABELS = {
  [EVENT_FORMAT.ONLINE]: 'Online Event',
  [EVENT_FORMAT.OFFLINE]: 'In person',
};

const isFilled = (value) => Boolean(String(value ?? '').trim());

export const formatPreviewEventDate = (eventDate) =>
  formatDate(eventDate, MOMENT_FORMATS.DATE_LONG_FULL) || '';

export const formatPreviewTimeRange = (startTime, endTime) => {
  const start = formatTimeLabel(startTime);
  const end = formatTimeLabel(endTime);
  if (start && end) return `${start} to ${end}`;
  return start || end || '';
};

export const getMenuSectionsForPreview = (menuSections = []) =>
  (Array.isArray(menuSections) ? menuSections : [])
    .map((section) => ({
      course: section.course,
      dishes: (section.dishes || [])
        .filter((dish) => isFilled(dish.dishName) || isFilled(dish.description))
        .map((dish) => ({
          id: dish.id,
          dishName: String(dish.dishName ?? '').trim(),
          description: formatMultilineDisplayText(dish.description),
          imagePreview: dish.imagePreview || '',
        })),
    }))
    .filter((section) => section.dishes.length > 0);

export const getEligibilityBadge = (attendeeEligibility) => {
  const eligibility = normalizeAttendeeEligibility(attendeeEligibility);
  if (eligibility === ATTENDEE_ELIGIBILITY.FEMALE) {
    return { icon: '\u2640', text: 'Female Only', variant: 'female' };
  }
  if (eligibility === ATTENDEE_ELIGIBILITY.MALE) {
    return { icon: '\u2642', text: 'Male Only', variant: 'male' };
  }
  return null;
};

const resolveHost = (partnerProfile, eventData) => {
  const record =
    partnerProfile?.partnerProfile && typeof partnerProfile.partnerProfile === 'object'
      ? { ...partnerProfile, ...partnerProfile.partnerProfile }
      : partnerProfile || eventData?.partner || eventData?.createdByPartner || null;

  if (!record) {
    return { name: 'Sure Space', avatarUrl: '' };
  }

  return {
    name: record.fullName || record.displayName || record.name || 'Sure Space',
    avatarUrl: record.avatarUrl || record.logoUrl || '',
  };
};

const buildJoinLabel = ({ requireJoinApproval }) => {
  return requireJoinApproval
    ? 'Request to Join this table - use coupon'
    : 'Join this table - use coupon';
};

export const buildEventLivePreviewModel = ({
  form,
  eventData = null,
  partnerProfile = null,
  isPartnerEvent = false,
} = {}) => {
  const totalSeats = Number(form?.totalSeats) || 0;
  const bookedUsers = Array.isArray(eventData?.bookedUsers) ? eventData.bookedUsers : [];
  const bookedCount = Number(eventData?.bookedSeats ?? bookedUsers.length ?? 0) || 0;
  const seatsRemaining = totalSeats > 0 ? Math.max(0, totalSeats - bookedCount) : 0;
  const menuSections = getMenuSectionsForPreview(form?.menuSections);
  const isOnline = isOnlineEventFormat(form?.eventFormat);
  const host = resolveHost(partnerProfile, eventData);
  const requireJoinApproval = Boolean(form?.requireJoinApproval);

  return {
    title: isFilled(form?.title) ? form.title.trim() : 'Event title',
    isTitlePlaceholder: !isFilled(form?.title),
    wingmanDescription: formatMultilineDisplayText(form?.wingmanDescription),
    description: formatMultilineDisplayText(form?.description),
    coverImageUrl: form?.coverImagePreview || '',
    dateLabel: formatPreviewEventDate(form?.eventDate) || 'Date to be set',
    isDatePlaceholder: !form?.eventDate,
    timeLabel: formatPreviewTimeRange(form?.startTime, form?.endTime) || 'Time to be set',
    isTimePlaceholder: !formatTimeLabel(form?.startTime) && !formatTimeLabel(form?.endTime),
    typeLabel: getEventTypeLabel(form?.type) || 'Event type',
    typeKey: form?.type || '',
    isTypePlaceholder: !form?.type,
    bookedCount: String(bookedCount),
    seatsRemaining,
    seatsRemainingLabel: `${seatsRemaining} seats left`,
    requireJoinApproval,
    eligibilityBadge: getEligibilityBadge(form?.attendeeEligibility),
    topics: normalizeTopicsFromApi(form?.topics),
    menuSections,
    menuCourseCount: menuSections.length,
    isOnline,
    eventFormatLabel: APP_FORMAT_LABELS[isOnline ? EVENT_FORMAT.ONLINE : EVENT_FORMAT.OFFLINE],
    venueName: String(form?.venueName ?? '').trim(),
    location: String(form?.location ?? '').trim(),
    showHost: Boolean(isPartnerEvent || eventData?.createdByPartnerId),
    hostName: host.name,
    hostAvatarUrl: host.avatarUrl,
    joinLabel: buildJoinLabel({ requireJoinApproval }),
  };
};
